import { Campaign } from '../types';

export const TOKEN_KEY = 'fic_admin_token';
export const EXPIRES_KEY = 'fic_admin_token_expires';

export class AdminSessionError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'AdminSessionError';
  }
}

export class AdminApiError extends Error {
  constructor() {
    super('Request failed');
    this.name = 'AdminApiError';
  }
}

export type LoginResult = 'ok' | 'credentials' | 'unavailable';

export interface SbicStatsResponse {
  total_issued: number;
  total_redeemed: number;
  redemption_rate: number;
  recent: Array<{
    id: string;
    code: string;
    redeemed: boolean;
    created_at: string | null;
    redeemed_at: string | null;
  }>;
}

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null) {
  unauthorizedHandler = handler;
}

export function getApiUrl(): string {
  const raw = process.env.REACT_APP_API_URL;
  if (typeof raw !== 'string' || raw.trim() === '') {
    throw new Error('REACT_APP_API_URL is not configured.');
  }
  return raw.replace(/\/+$/, '');
}

/**
 * Backend POST /admin/login returns expires_at as an ISO-8601 string
 * (new Date(exp).toISOString()). This is the single client normalizer
 * used before expiration comparison.
 */
export function parseExpiresAt(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw < 1e12 ? raw * 1000 : raw;
  }
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const asNumber = Number(trimmed);
  if (Number.isFinite(asNumber) && trimmed === String(asNumber)) {
    return asNumber < 1e12 ? asNumber * 1000 : asNumber;
  }
  const ms = Date.parse(trimmed);
  return Number.isFinite(ms) ? ms : null;
}

export function clearStoredSession() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(EXPIRES_KEY);
}

export function readStoredSession(): { token: string; expiresAtMs: number } | null {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const expiresAtMs = parseExpiresAt(sessionStorage.getItem(EXPIRES_KEY));
  if (!token || expiresAtMs == null) return null;
  if (Date.now() >= expiresAtMs) {
    clearStoredSession();
    return null;
  }
  return { token, expiresAtMs };
}

function notifyUnauthorized() {
  clearStoredSession();
  unauthorizedHandler?.();
}

export async function loginWithPassword(password: string): Promise<LoginResult> {
  let base: string;
  try {
    base = getApiUrl();
  } catch {
    return 'unavailable';
  }

  let response: Response;
  try {
    response = await fetch(`${base}/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
  } catch {
    return 'unavailable';
  }

  if (response.status === 401) return 'credentials';
  if (!response.ok) return 'unavailable';

  let data: { token?: unknown; expires_at?: unknown };
  try {
    data = await response.json();
  } catch {
    return 'unavailable';
  }

  if (typeof data.token !== 'string' || data.token.length === 0) return 'unavailable';
  const expiresAtMs = parseExpiresAt(data.expires_at);
  if (expiresAtMs == null) return 'unavailable';

  sessionStorage.setItem(TOKEN_KEY, data.token);
  sessionStorage.setItem(
    EXPIRES_KEY,
    typeof data.expires_at === 'string' ? data.expires_at : new Date(expiresAtMs).toISOString(),
  );
  return 'ok';
}

export async function logoutRemote() {
  try {
    const base = getApiUrl();
    await fetch(`${base}/admin/logout`, { method: 'POST' });
  } catch {
    // Client-side session clear is authoritative.
  }
}

async function adminFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const base = getApiUrl();
  const session = readStoredSession();
  if (!session) {
    notifyUnauthorized();
    throw new AdminSessionError();
  }

  let response: Response;
  try {
    response = await fetch(`${base}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.token}`,
        ...(init.headers || {}),
      },
    });
  } catch {
    throw new AdminApiError();
  }

  if (response.status === 401) {
    notifyUnauthorized();
    throw new AdminSessionError();
  }

  return response;
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export async function fetchSession(): Promise<boolean> {
  try {
    const response = await adminFetch('/admin/session');
    return response.ok;
  } catch (err) {
    if (err instanceof AdminSessionError) return false;
    throw err;
  }
}

export async function listCampaigns(): Promise<Campaign[]> {
  const response = await adminFetch('/admin/campaigns');
  if (!response.ok) throw new AdminApiError();
  const data = (await readJson(response)) as { campaigns?: Campaign[] } | null;
  if (!data || !Array.isArray(data.campaigns)) return [];
  return data.campaigns;
}

export async function createCampaign(body: Omit<Campaign, 'id'>): Promise<void> {
  const response = await adminFetch('/admin/campaigns', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new AdminApiError();
}

export async function updateCampaign(id: string, body: Omit<Campaign, 'id'> | { active: boolean }): Promise<void> {
  const response = await adminFetch(`/admin/campaigns/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new AdminApiError();
}

export async function deleteCampaign(id: string): Promise<void> {
  const response = await adminFetch(`/admin/campaigns/${id}`, { method: 'DELETE' });
  if (!response.ok) throw new AdminApiError();
}

export async function fetchSbicStats(campaignId: string): Promise<SbicStatsResponse> {
  const response = await adminFetch(`/admin/campaigns/${campaignId}/sbic-stats`);
  if (!response.ok) throw new AdminApiError();
  const data = (await readJson(response)) as SbicStatsResponse | null;
  if (!data || !Array.isArray(data.recent)) throw new AdminApiError();
  return data;
}
